(() => {
  if (typeof window.customElements.get('product-section') !== 'undefined') { return }

  class ProductSection extends HTMLElement {
    constructor() {
      super()

      this.productSelect = this.querySelector('[data-product-select]')
      this.productPriceEls = this.querySelectorAll('[data-product-price]')
      this.perOffBadges = this.querySelectorAll('[data-per-badge]')
      this.saleBadge = this.querySelector('[data-sale-badge]')
      this.productComparePriceEls = this.querySelectorAll('[data-product-compare-price]')
      this.paymentPlanWrapperEls = this.querySelectorAll('[data-payment-plan-wrapper]')
      this.productSubscribeComparePriceEls = this.querySelectorAll('[data-product-non-subscription-price]')
      this.productSubscribePriceEl = this.querySelector('[data-product-subscribe-price]')
      this.subscriptionDiscountEl = this.querySelector('[data-product-discount-percentage-setting]')
      this.subscriptionStrikedPrice = this.querySelector('[data-product-non-subscription-price-strike]')
      this.subscriptionOptions = this.querySelector('[data-product-subscription-options]')
      this.sectionId = this.getAttribute('data-section-id')
      this.submitButton = this.querySelector('[data-product-submit]')
      this.submitButtonTextEls = this.querySelectorAll('[data-product-submit-text]')
      this.productGallery = this.querySelector('[data-product-gallery]')
      this.quantityNotif = this.querySelector('[data-quantity-notif]')
      this.contactsQuantitySelectors = this.querySelectorAll('[data-contacts-quantity-selector]')
      this.selectedVariantData = null
      this.selectedVariantSellingPlan = null
      this.isSellingPlanSelected = false
      this.productData = null
      this.quantity = 1
      this.stepper = this.querySelector('prescription-glasses-stepper')
      this.stepperToggle = this.querySelector('[data-open-stepper]')

      // Sticky Form on non-frame, non-contact products
      this.stickyImage = this.querySelector('[data-sticky-image]')
      this.stickyButton = this.querySelector('[data-product-sticky-atc]')

      this.initProductSection()
    }

    async initProductSection() {
      try {
        this.productData = await fetch(`/products/${this.dataset.productHandle}.js`)
          .then(res => res.json())
        this.productFormInit()
        this.productOptionsInit()
        this.contactsQuantityInit()
        this.updateQuantity()
        this.lensSelectScroll()
        this.initStickyAtc()
        this.initStepperToggle()
      } catch (error) {
        console.error(error)
      }
    }

    productFormInit() {
      const currentUrl = new URL(document.location)

      this.productSelect.addEventListener('change', () => {
        this.getCurrentVariantData()
        this.updateProductPrices()
        this.updateButtonState()
        this.toggleProductGalleries()
        this.updateQuantityNotif()
        this.updateStickyImage()
        this.updateWindowProduct()

        if (window.location.href.indexOf('/products/') >= 0) {
          currentUrl.searchParams.set('variant', this.selectedVariantData.id)
          window.history.replaceState(null, null, currentUrl.toString())
        }

        localStorage.setItem("colorVariant", this.selectedVariantData.id);
        localStorage.setItem("variantUPC", this.selectedVariantData.barcode);
      })

      this.getCurrentVariantData()
      this.subscriptionOptionsInit()
      this.updateQuantityNotif()
      this.updateStickyImage()
    }

    updateWindowProduct() {
      window.productInformation.title = this.selectedVariantData.name
      localStorage.setItem("productDetails", JSON.stringify(window.productInformation));
    }

    updateStickyImage() {
      if (!this.stickyImage) { return }
      this.stickyImage.setAttribute('src', this.selectedVariantData.featured_image.src)
    }

    initStickyAtc() {
      if (!this.stickyButton) { return }
      this.stickyButton.addEventListener('click', () => {
        if (this.submitButton.disabled) {
          this.submitButton.scrollIntoView({ behavior: 'smooth', block: 'center' })
          return
        }
        this.submitButton.click()
      })
    }

    // This is to make the old Lens form visible as it is functionally a popup.
    // TODO: Remove this logic once new form logic is added.
    lensSelectScroll() {
      if (!this.submitButton?.hasAttribute('data-product-scroll-to-top')) { return }
      this.submitButton.addEventListener('click', () => {
        window.scrollTo(0, 0)
      })
    }

    updateButtonState() {
      if (!this.submitButton || !this.selectedVariantData) { return }
      const buttonState = this.selectedVariantData.available ? 'add' : 'sold-out'

      this.submitButton.disabled = !this.selectedVariantData.available
      this.submitButtonTextEls.forEach(textEl => textEl.classList.toggle('hide', buttonState !== textEl.dataset.state))
    }

    getCurrentVariantData() {
      const variantId = this.productSelect.value
      this.selectedVariantData = this.productData.variants.find(variant => variant.id === Number(variantId))
    }

    productOptionsInit() {
      const optionsParentEl = this.querySelector('[data-product-options]')
      const optionGroups = this.querySelectorAll('[data-product-option-group]')
      const optionRadios = this.querySelectorAll('[data-product-option][data-type="radio"]')
      const variantStockMessage = this.querySelector('[data-variant-stock-message]')
      const quantitySelectInput = this.querySelector('[data-quantity-input]')
      const bisEl = this.querySelector('bis-form')

      if (!optionsParentEl || !optionGroups.length) { return }

      const unavailableDisplay = optionsParentEl.getAttribute('data-unavailable-display')

      const getNextExistingVariant = (selectedOptions, optionIndex) => {
        const nextVariant = this.productData.variants.find(variant => variant[`option${optionIndex}`] === selectedOptions[`option${optionIndex}`])
        const nextVariantOptions = nextVariant.options.reduce((object, option, index) => {
          object[`option${index + 1}`] = option
          return object
        }, {})

        return {
          nextVariantOptions,
          nextVariant
        }
      }

      const getSelectedOptions = () => {
        const selectedOptions = [...optionGroups].reduce((object, group) => {
          let value = null
          if (group.dataset.type === 'select') {
            const select = group.querySelector('[data-product-option]')
            value = select.value
          } else {
            value = group.querySelector('[data-product-option]:checked').value
          }

          object[`option${group.dataset.optionIndex}`] = value
          return object
        }, {})

        return selectedOptions
      }

      const updateSelectedText = (group, input) => {
        optionGroups.forEach(optionGroup => {
          const selectedInput = optionGroup.querySelector('[data-product-option]:checked')
          const selectedTextEl = optionGroup.querySelector('[data-product-option-group-selected-text]')
          if (!selectedTextEl || !selectedInput) return

          const selectedText = selectedInput.dataset.selectedText

          selectedTextEl.textContent = selectedText
        })
      }

      const updateVariantStockMessage = (policy, quantity) => {
        const variantQuantity = parseInt(quantity, 10)

        if (!variantStockMessage) return
        variantStockMessage.classList.add('hide')

        if (policy === 'deny' && variantQuantity > 0 && variantQuantity < parseInt(variantStockMessage.dataset.lowStockBreakpoint, 10)) {
          variantStockMessage.classList.remove('hide')
        }
      }

      const updateQuanitySelect = (selectedVariant) => {
        if (!quantitySelectInput || !selectedVariant) { return }

        const selectedOption = this.productSelect.querySelector(`option[value="${selectedVariant.id}"]`)
        const { inventoryQuantity, inventoryPolicy } = selectedOption.dataset

        if (quantitySelectInput.value > inventoryQuantity && inventoryQuantity > 0) {
          quantitySelectInput.value = inventoryQuantity
        }

        const maxValue = inventoryPolicy === 'continue' ? 99 : inventoryQuantity

        quantitySelectInput.max = maxValue

        updateVariantStockMessage(inventoryPolicy, inventoryQuantity)
      }

      const updateOptionRadios = (selectedOptions) => {
        optionRadios.forEach((optionRadio, index) => {
          const inputOptions = { ...selectedOptions }
          const parentItemEl = optionRadio.closest('[data-product-option-item]')
          const optionIndex = Number(optionRadio.dataset.optionIndex)

          inputOptions[`option${optionIndex}`] = optionRadio.value

          const matchingVariant = getVariantByOptions(inputOptions)

          optionRadio.checked = optionRadio.value === selectedOptions[`option${optionIndex}`]

          parentItemEl.classList.toggle('unavailable', !matchingVariant)
          parentItemEl.classList.toggle('soldout', matchingVariant?.available === false)
        })
      }

      const getVariantByOptions = (options) => {
        const matchingVariant = this.productData.variants.find(variant => {
          let isMatch = true

          for (const [key, value] of Object.entries(options)) {
            if (variant[key] !== value) {
              isMatch = false
              break
            }
          }

          return isMatch
        })

        return matchingVariant || null
      }

      const setAtcButtonUnavailable = () => {
        if (!this.submitButton) { return }
        const buttonState = 'unavailable'
        this.submitButton.disabled = true
        this.submitButtonTextEls.forEach(textEl => textEl.classList.toggle('hide', buttonState !== textEl.dataset.state))
      }

      optionGroups.forEach(optionGroup => {
        optionGroup.addEventListener('change', (event) => {
          handleOptionChange(optionGroup)
        })
      })

      const handleOptionChange = (group) => {
        let selectedOptions = getSelectedOptions()
        let selectedVariant = getVariantByOptions(selectedOptions)

        if (!selectedVariant && unavailableDisplay === 'select-next-available') {
          const nextVariantData = getNextExistingVariant(selectedOptions, group.dataset.optionIndex)
          selectedOptions = nextVariantData.nextVariantOptions
          selectedVariant = nextVariantData.nextVariant
        }

        updateOptionRadios(selectedOptions)
        updateSelectedText()
        updateQuanitySelect(selectedVariant)

        if (bisEl) {
          bisEl.handleVariantUpdate(selectedVariant)
        }

        if (selectedVariant) {
          this.productSelect.value = selectedVariant.id
          this.productSelect.dispatchEvent(new Event('change'))
        } else {
          setAtcButtonUnavailable()
        }
      }
      handleOptionChange(optionGroups[0])
    }

    updateProductPrices() {
      let price = this.selectedVariantData.price
      let comparePrice = this.selectedVariantData.compare_at_price

      if (this.isSellingPlanSelected && this.selectedVariantSellingPlan) {
        price = this.selectedVariantSellingPlan.price
        comparePrice = this.selectedVariantSellingPlan.compare_at_price
      }

      // Base prices
      this.productPriceEls.forEach(productPriceEl => {
        const priceToShow = productPriceEl.hasAttribute('data-single-unit') ? price : price * this.quantity
        productPriceEl.textContent = `${this.formatMoney(priceToShow)}${productPriceEl.dataset.suffix ? ' ' + productPriceEl.dataset.suffix : ''}`
      })

      // Compare Price
      this.productComparePriceEls.forEach(priceEl => {
        const comparePriceToShow = priceEl.hasAttribute('data-single-unit') ? comparePrice : comparePrice * this.quantity
        priceEl.classList.toggle('hide', !comparePrice || price >= comparePrice)
        priceEl.textContent = this.formatMoney(comparePriceToShow)
      })

      // Percent Off Badge
      this.perOffBadges.forEach(perOffBadge => {
        perOffBadge.classList.toggle('hide', !comparePrice || price >= comparePrice)
        perOffBadge.textContent = `${Math.round(100 * (comparePrice - price) / comparePrice)}% Off`
      })

      // Payment Plans
      this.paymentPlanWrapperEls.forEach(paymentPlanWrapperEl => {
        paymentPlanWrapperEl.classList.toggle('hide', !this.selectedVariantData)

        const paymentPlanPriceEl = paymentPlanWrapperEl.querySelector('[data-payment-plan-price]')
        if (!paymentPlanPriceEl) return

        paymentPlanPriceEl.innerHTML = `${(this.formatMoney((price * this.quantity) / 4))}`
      });

      // Sale Badge
      this.saleBadge?.classList.toggle('hide', !comparePrice || price >= comparePrice)
    }

    subscriptionOptionsInit() {
      const sellingPlansGroupWrap = this.querySelector('[data-product-selling-plan-group-wrap]')
      const sellingPlansGroupEls = this.querySelectorAll('[data-product-selling-plan-group]')
      if (!sellingPlansGroupEls.length) { return }

      const subscriptionToggleRadios = this.querySelectorAll('[data-product-subscription-toggle]')
      const sellingPlanHiddenInput = this.querySelector('[data-product-selling-plan-input]')

      let currentSellingPlanGroupEl = null
      let currentSellingPlanSelect = null

      sellingPlansGroupEls.forEach(sellingPlansGroupEl => {
        const sellingPlanSelect = sellingPlansGroupEl.querySelector('[data-product-selling-plans-select]')

        this.selectedVariantSellingPlan = this.selectedVariantData.selling_plan_allocations.find(allocation => allocation.selling_plan_id === Number(sellingPlanSelect.value))

        sellingPlanSelect.addEventListener('change', () => {
          sellingPlanHiddenInput.value = sellingPlanSelect.value
          this.selectedVariantSellingPlan = this.selectedVariantData.selling_plan_allocations.find(allocation => allocation.selling_plan_id === Number(sellingPlanSelect.value))
          window.sellingPlan = this.selectedVariantSellingPlan.selling_plan_id
          this.updateProductPrices()
        })
      })

      const updateCurrentSellingPlan = () => {
        const oneTimeToggle = this.querySelector('[data-product-subscription-toggle][value="one-time"]')
        const subscriptionToggleWrapper = this.querySelector('[data-product-subscription-toggle][value="subscription"]').closest('[data-subscription-toggle-wrapper]')

        subscriptionToggleWrapper.classList.remove('hide')

        const variantSellingPlanGroupIds = this.selectedVariantData.selling_plan_allocations.map(allocation => allocation.selling_plan_group_id)

        currentSellingPlanGroupEl = [...sellingPlansGroupEls].find(groupEl => variantSellingPlanGroupIds.includes(groupEl.dataset.id))

        if (currentSellingPlanGroupEl) {
          currentSellingPlanSelect = currentSellingPlanGroupEl.querySelector('[data-product-selling-plans-select]')
          this.selectedVariantSellingPlan = this.selectedVariantData.selling_plan_allocations.find(allocation => allocation.selling_plan_id === Number(currentSellingPlanSelect.value))
        }

        if (oneTimeToggle && oneTimeToggle.checked) {
          sellingPlanHiddenInput.value = ''

          if (!currentSellingPlanGroupEl) {
            subscriptionToggleWrapper.classList.add('hide')
          }
        } else if (currentSellingPlanGroupEl) {
          sellingPlanHiddenInput.value = currentSellingPlanSelect.value
        } else if (oneTimeToggle) {
          oneTimeToggle.click()
          sellingPlanHiddenInput.value = ''
          subscriptionToggleWrapper.classList.add('hide')
        }
      }

      const updateSellingPlanGroups = () => {
        sellingPlansGroupEls.forEach(groupEl => groupEl.classList.toggle('hide', this.selectedVariantSellingPlan && groupEl.dataset.id !== this.selectedVariantSellingPlan.selling_plan_group_id))
      }

      subscriptionToggleRadios.forEach(toggleRadio => {
        toggleRadio.addEventListener('change', () => {
          this.isSellingPlanSelected = toggleRadio.value === 'subscription'

          window.orderType = toggleRadio.value === 'one-time' ? 'oneTime' : 'subscribe'
          window.sellingPlan = toggleRadio.value === 'one-time' ? false : window.sellingPlan

          sellingPlansGroupWrap.classList.toggle('hide', !this.isSellingPlanSelected)

          updateCurrentSellingPlan()
          updateSellingPlanGroups()
          this.updateProductPrices()
        })
      })

      this.productSelect.addEventListener('change', (event) => {
        updateCurrentSellingPlan()
        updateSellingPlanGroups()
        this.updateSellingPlanPrices()
        this.updateProductPrices()
        this.toggleProductGalleries()
        this.updateQuantityNotif()
        this.updateWindowProduct()
      })
    }

    updateSellingPlanPrices() {
      if (!this.selectedVariantSellingPlan) { return }
      // One time purchase price
      this.productSubscribeComparePriceEls.forEach(priceEl => {
        priceEl.textContent = this.formatMoney(this.selectedVariantData.price * this.quantity)
        priceEl.classList.toggle('hide', priceEl.hasAttribute('data-product-non-subscription-price-hide') && this.selectedVariantData.price >= this.selectedVariantSellingPlan.price)
      })

      // Subscription price
      if (this.isNumber(Number(this.subscriptionDiscountEl.textContent))) {
        const pricedAtAmount = this.selectedVariantSellingPlan.price * this.quantity
        const discountAmount = this.calculatePercentage(Number(this.subscriptionDiscountEl.textContent), Number(this.selectedVariantSellingPlan.price * this.quantity))
        this.productSubscribePriceEl.textContent = this.formatMoney(pricedAtAmount - discountAmount)
        this.subscriptionStrikedPrice.textContent = this.formatMoney(pricedAtAmount)
      }
    }

    contactsQuantityInit() {
      if (!this.contactsQuantitySelectors.length) { return }
      this.quantity = 0

      this.contactsQuantitySelectors.forEach(contactsQuantitySelector => {
        const buttons = contactsQuantitySelector.querySelectorAll('[data-contacts-quantity]')

        buttons.forEach(button => {
          const amount = Number(button.dataset.contactsQuantity)
          this.quantity = this.quantity + amount

          button.addEventListener('click', () => {
            this.updateContactsQuantity(contactsQuantitySelector, amount)
          })
        });
      });
    }

    updateContactsQuantity(contactsQuantitySelector, amount) {
      let newAmount = Number(contactsQuantitySelector.dataset.amount) + amount
      newAmount = newAmount < 0 ? 0 : newAmount
      contactsQuantitySelector.setAttribute('data-amount', newAmount)

      const span = contactsQuantitySelector.querySelector('span')
      span.textContent = `${newAmount} ${newAmount === 1 ? contactsQuantitySelector.dataset.textSingle : contactsQuantitySelector.dataset.textMultiple}`

      this.updateQuantity()
    }

    updateQuantity() {
      this.quantity = 1
      if (!this.contactsQuantitySelectors.length) {
        this.updateProductPrices()
        this.updateSellingPlanPrices()
        return
      }

      this.quantity = [...this.contactsQuantitySelectors].reduce((total, contactsQuantitySelector) => total + Number(contactsQuantitySelector.dataset.amount), 0)
      this.updateProductPrices()
      this.updateSellingPlanPrices()
    }

    toggleProductGalleries() {
      const color = this.selectedVariantData.options[(this.dataset.colorOptionIndex - 1)]
      const variantGrids = this.productGallery.querySelectorAll('[data-product-gallery-grid]')
      variantGrids.forEach(variantGrid => {
        variantGrid.classList.toggle('hide', color !== variantGrid.dataset.color)
      });
      this.productGallery.update()
    }

    updateQuantityNotif() {
      if (!this.quantityNotif) return

      const onlineAvailableWrapper = this.quantityNotif.querySelector('[data-qty-online-available]')
      const onlineOosWrapper = this.quantityNotif.querySelector('[data-qty-online-oos]')
      const onlineQtySpans = this.quantityNotif.querySelectorAll('[data-online-qty]')

      const clinicAvailableWrapper = this.quantityNotif.querySelector('[data-qty-clinic-available]')
      const clinicOosWrapper = this.quantityNotif.querySelector('[data-qty-clinic-oos]')
      const clinicQtySpans = this.quantityNotif.querySelectorAll('[data-clinic-qty]')

      const selectedOption = this.productSelect.querySelector(`option[value="${this.selectedVariantData.id}"]`)
      const inventoryQuantity = selectedOption.dataset.inventoryQuantity
      const inventoryPolicy = selectedOption.dataset.inventoryPolicy

      onlineAvailableWrapper.classList.toggle('hide', inventoryQuantity <= 0)
      onlineOosWrapper.classList.toggle('hide', inventoryQuantity > 0)

      onlineQtySpans.forEach(onlineQtySpan => {
        onlineQtySpan.classList.toggle('hide', inventoryPolicy === 'continue')
        onlineQtySpan.textContent = inventoryQuantity < 0 ? 0 : inventoryQuantity
      });

      // TODO: Add clinic logic once API is running
    }

    formatMoney(money) {
      return `$${(money * 0.01).toFixed(2)}`
    }

    initStepperToggle() {
      this.stepperToggle && this.stepperToggle.addEventListener('click', () => {
        this.stepper.open()
      })
    }

    isNumber(value) {
      return typeof value === 'number' && !isNaN(value);
    }

    calculatePercentage(percentage, total) {
      if (typeof percentage !== 'number' || typeof total !== 'number') {
        throw new Error('Both percentage and total must be numbers');
      }
      return (percentage / 100) * total;
    }
  }

  window.customElements.define('product-section', ProductSection)
})()
